/**
 * @file CustomerOrdersSection.tsx
 * @description Вкладка «Заказы» карточки клиента (phase19.6; phase19.31 —
 * SC63, SC68): таблица от 1024 / компактный список, постранично
 * (`CrmPagination`), оплата — колонкой. Строка — ссылка на карточку заказа.
 * Колонки — `CUSTOMER_ORDER_COLUMNS`.
 *
 * Блок «Последние заказы» на «Обзоре» заменён «Сейчас в работе» (SC68,
 * только открытые заказы из сводки клиента) — режим `recent` убран.
 */

import { ShoppingBag } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router";

import { CrmPagination } from "../../../../components/crm/CrmPagination";
import { CUSTOMER_ORDER_COLUMNS } from "../../../../components/customers/card/customer-order-columns";
import { ResponsiveList } from "../../../../components/ui/ResponsiveList";
import { ApiError } from "../../../../lib/api-client";
import { customers, type CustomerOrderListItemDto } from "../../../../lib/api-customers";
import { CRM_DEFAULT_PAGE_SIZE } from "../../../../lib/crm-list-query";

export function CustomerOrdersSection({ customerId }: { customerId: string }) {
  const { slug } = useParams<{ slug: string }>();
  const [items, setItems] = useState<CustomerOrderListItemDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(CRM_DEFAULT_PAGE_SIZE);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    customers
      .orders(customerId, { limit: pageSize, offset: (page - 1) * pageSize })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Не удалось загрузить заказы клиента");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, page, pageSize, attempt]);

  return (
    <div className="flex flex-col gap-3">
      <ResponsiveList
        aria-label="Заказы клиента"
        rows={items}
        columns={CUSTOMER_ORDER_COLUMNS}
        rowKey={(o) => o.id}
        rowHref={(o) => `/shop/${slug}/orders/${o.id}`}
        rowLabel={(o) => `Заказ ${o.number}`}
        rowSize="double"
        layout="auto"
        framed
        loading={loading}
        error={error}
        onRetry={() => setAttempt((n) => n + 1)}
        skeletonRows={5}
        empty={{ icon: ShoppingBag, title: "Заказов пока нет" }}
      />
      <CrmPagination
        label="Страницы заказов клиента"
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </div>
  );
}
